import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { ProgramFrmComponent } from './program-frm.component';
import { AgFns, GridOptionsExt, ISortModel, ProductFns, ProxAgFns, RowSelectionMultipleCheckbox, UtilDialogService } from '@core';
import { ActiveStatus, KitDetail, KitTag, ProgramKit } from '@models';
import { ColDef, GridReadyEvent, RowSelectedEvent } from '@ag-grid-community/core';
import { ToastrService } from 'ngx-toastr';
import { AccountDbQueryService } from '../services/account-db-query.service';
import { AccountDbSaveService } from '../services/account-db-save.service';
import { KitFinderDialog } from './program-kit-finder.dialog';
import { Guid } from 'guid-typescript';
import { ProgramKitTagPickerDialogComponent } from './program-kit-tag-picker.dialog.component';
import { EntityFns } from '@data';
import * as _ from 'lodash';

@Component({
  selector: 'prox-program-kits-sub',
  standalone: false,
  templateUrl: './program-kits.sub.component.html',
})
export class ProgramKitsSubComponent implements OnInit, OnChanges {
  @Input() parent!: ProgramFrmComponent;
  @Input() visible!: boolean;

  activeStatuses!: ActiveStatus[];

  kitsGridOptions: GridOptionsExt = {};
  programKits: ProgramKit[] = [];
  selectedProgramKit!: ProgramKit;

  kitDetailsGridOptions: GridOptionsExt = {};
  kitDetails: KitDetail[] = [];
  kitTags: KitTag[] = [];

  get program() {
    return this.parent.program;
  }

  constructor(
    public dbQueryService: AccountDbQueryService,
    public dbSaveService: AccountDbSaveService,
    public dialogService: UtilDialogService,
    private toastr: ToastrService,
  ) {}

  async ngOnInit() {
    this.kitsGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onKitsGridReady,
      onRowSelected: this.onKitRowSelected,
      rowModelType: 'clientSide',
    });

    this.kitDetailsGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onKitDetailsGridReady,
      onRowSelected: this.onKitDetailRowSelected,
      rowModelType: 'clientSide',
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.refresh();
  }

  async refresh(forceRefresh = false) {
    if (!this.visible && !forceRefresh) return;
    this.parent.updateComponentNameMap(this);

    await this.parent.wrapBusy(async () => {
      this.activeStatuses = await this.dbQueryService.getAll(ActiveStatus);

      const isLoaded = this.program.entityAspect.isNavigationPropertyLoaded('programKits');
      if (isLoaded && !forceRefresh) {
        this.programKits = this.program.programKits;
      } else {
        this.programKits = await this.dbQueryService.getProgramKitsByProgramId(this.program.id);
        this.program.entityAspect.markNavigationPropertyAsLoaded('programKits');
      }

      this.kitTags = this.parent.allKitTags;

      this.onKitsGridReady();
      this.onKitDetailsGridReady();

      await AgFns.refreshGrid(this.kitsGridOptions, this.programKits);
      await AgFns.refreshGrid(this.kitDetailsGridOptions, this.kitDetails);

      AgFns.selectDefaultRow(this.kitsGridOptions);
    });
  }

  onKitsGridReady(event?: GridReadyEvent) {
    const [colDefs, sortModel] = this.getKitsColDefsAndSortModel();
    AgFns.initGrid(event ?? this.kitsGridOptions, colDefs, sortModel);
  }

  onKitDetailsGridReady(event?: GridReadyEvent) {
    const [colDefs, sortModel] = this.getKitDetailsColDefsAndSortModel();
    AgFns.initGrid(event ?? this.kitDetailsGridOptions, colDefs, sortModel);
  }

  onKitRowSelected(event: RowSelectedEvent) {
    if (!event.node.isSelected()) return;
    this.selectedProgramKit = event.data;
    this.kitDetails = event.data.kit.kitDetails;
  }

  onKitDetailRowSelected() {}

  getKitsColDefsAndSortModel() {
    const colDefs: ColDef[] = [
      { headerName: 'Name', field: 'kit.name', filter: 'agTextColumnFilter', ...AgFns.flexWidth(200) },
      { headerName: 'Description', field: 'kit.description', filter: 'agTextColumnFilter', ...AgFns.flexWidth(400) },
      {
        ...AgFns.createButtonProps('Kit Tags', this.onTags.bind(this), {
          calcLabel: this.calcTagsLabel.bind(this),
          calcDisabled: () => this.parent.isReadOnly(),
        }),
        ...AgFns.flexWidth(150),
      },
      { ...ProxAgFns.getActiveStatusColDef('kit.activeStatus.name'), filter: 'agTextColumnFilter', ...AgFns.fixWidth(200) },
      this.parent.ifEditable(ProxAgFns.getEntityDeleteColDef(this.onPkDelete.bind(this))),
    ];
    const sortModel: ISortModel = [{ colId: 'name', sort: 'asc' }];
    return [colDefs, sortModel] as const;
  }

  async onTags(pk: ProgramKit) {
    await this.dialogService.createFinder(ProgramKitTagPickerDialogComponent, {
      tags: this.parent.allKitTags,
      parents: [pk],
      rowSelection: RowSelectionMultipleCheckbox,
    });
  }

  calcTagsLabel(pk: ProgramKit) {
    if (pk.entityAspect.entityState.isDeleted()) {
      return '';
    }

    return pk.tags == '' ? ' - NONE - ' : pk.tags;
  }

  getKitDetailsColDefsAndSortModel() {
    const colDefs: ColDef[] = [
      ProxAgFns.getEntityStateColDef(),
      {
        headerName: 'Manufacturer',
        field: 'productTypeConfigProduct.productTypeConfig.pricedProductType.productType.manufacturer.name',
        filter: 'agTextColumnFilter',
        ...AgFns.flexWidth(200),
      },
      // {
      //   headerName: 'SKU',
      //   field: 'productTypeConfigProduct.product.productType.productKey',
      //   filter: 'agTextColumnFilter',
      //   ...AgFns.fixWidth(200),
      // },
      {
        headerName: 'Name',
        field: 'productTypeConfigProduct.product.productType.name',
        filter: 'agTextColumnFilter',
        ...AgFns.flexWidth(200),
      },
      {
        headerName: 'Configuration',
        field: 'productTypeConfigProduct.productTypeConfig.name',
        filter: 'agTextColumnFilter',
        ...AgFns.fixWidth(200),
      },
      {
        headerName: 'Description',
        field: 'productTypeConfigProduct.product.productType.description',
        filter: 'agTextColumnFilter',
        ...AgFns.flexWidth(350),
      },
      ProductFns.getAllFeaturesColDef((x) => {
        return x.data.productTypeConfigProduct.product;
      }),
      { headerName: 'Qty', field: 'quantity', ...AgFns.fixWidth(120), editable: false },
      {
        headerName: 'Unit Price',
        colId: 'unitPrice',
        ...AgFns.fixWidth(150),
        valueGetter: (p) => p.data.getPriceInfo().totalUnitPrice,
      },
      {
        ...ProxAgFns.getActiveStatusColDef('productTypeConfigProduct.productTypeConfig.activeStatus.name'),
        filter: 'agTextColumnFilter',
        ...AgFns.fixWidth(150),
      },
    ];
    const sortModel: ISortModel = [{ colId: 'name', sort: 'asc' }];
    return [colDefs, sortModel] as const;
  }

  async onPkDelete(pk: ProgramKit) {
    if (!this.programKits) return;
    pk.programKitTagMaps.slice().forEach((x) => EntityFns.deleteOrDetach(x.entityAspect));
    EntityFns.deleteOrDetach(pk.entityAspect);
    _.remove(this.programKits, pk);
    this.kitDetails = [];
    AgFns.refreshGrid(this.kitsGridOptions, this.programKits);
    AgFns.refreshGrid(this.kitDetailsGridOptions, this.kitDetails);
  }

  async onApply() {
    if (!this.parent.program.id) return [];
    const foundKits = await this.parent.dialogService.createFinder(KitFinderDialog, {
      accountId: this.program.accountId,
      rowSelection: RowSelectionMultipleCheckbox,
    });
    if (foundKits.length == 0) return [];

    const existingKitIds = new Set(this.programKits.map((pk) => pk.kitId));
    const newKits = foundKits.filter((x) => !existingKitIds.has(x.id));
    if (newKits.length === 0) {
      this.toastr.warning('All selected kits are already applied to this program.');
      return this.programKits;
    }
    if (newKits.length < foundKits.length) {
      this.toastr.warning('Some selected kits were already applied and were skipped.');
    }
    const kits = newKits.map((x) => {
      return this.dbSaveService.createEntity(ProgramKit, {
        id: Guid.create().toString(),
        programId: this.program.id,
        kitId: x.id,
      });
    });
    this.programKits?.push(...kits);
    this.kitDetails = this.programKits[0].kit.kitDetails;
    AgFns.refreshGrid(this.kitsGridOptions, this.programKits);
    AgFns.refreshGrid(this.kitDetailsGridOptions, this.kitDetails);
    return this.programKits;
  }
}
